import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { brandColor, brandInitial } from '../utils/brand'
import { loadBrands } from '../services/brandStore'
import { loadVcards } from '../services/vcardStore'
import type { Brand, Vcard } from '../types'

/** Brand-Verwaltung für den Admin — Liste aller Brands mit Bearbeiten-Zugang (Spec §5.6).
 *  BMC-Designsystem `console.css`. */
export function BrandListPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const [brands, setBrands] = useState<Brand[] | null>(null)
  const [vcards, setVcards] = useState<Vcard[]>([])

  useEffect(() => {
    if (!session) return
    let cancelled = false
    Promise.all([loadBrands(session), loadVcards(session)]).then(([brandList, vcardList]) => {
      if (cancelled) return
      setBrands(brandList)
      setVcards(vcardList)
    })
    return () => {
      cancelled = true
    }
  }, [session])

  if (!session) return null
  const gate = can(session)

  return (
    <>
      <section className="cover">
        <div className="eyebrow">Administration</div>
        <h1>
          Alle <span className="hl">Marken</span> verwalten
        </h1>
        <p>Verwalte hier sämtliche Brands. Neue Brands legt nur der Admin an.</p>
      </section>

      <section className="panel">
        <div className="head">
          <h2>Brands</h2>
          <span className="sub">{brands ? brands.length : '–'}</span>
          <span className="spacer" />
          {gate.brandCreate() && (
            <button type="button" className="btn primary sm" onClick={() => navigate('/admin/brands/new')}>
              {Icons.plus} Neue Brand
            </button>
          )}
        </div>

        <div className="list">
          {brands === null ? (
            <div className="empty">Brands werden geladen …</div>
          ) : brands.length === 0 ? (
            <div className="empty">Keine Brands vorhanden.</div>
          ) : (
            brands.map((b) => {
              const vcardCount = vcards.filter((v) => v.brandKey === b.brandKey).length
              const editable = gate.brandEdit(b.brandKey)
              const goToBrand = (): void => {
                if (editable) navigate(`/admin/brands/${b.brandKey}`)
              }
              return (
                <div
                  key={b.brandKey}
                  className="lrow"
                  role={editable ? 'button' : undefined}
                  aria-label={editable ? `Brand „${b.brandName}" bearbeiten` : undefined}
                  tabIndex={editable ? 0 : undefined}
                  style={editable ? undefined : { cursor: 'default' }}
                  onClick={goToBrand}
                  onKeyDown={(e) => {
                    if (editable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      goToBrand()
                    }
                  }}
                >
                  <span className="pic" style={{ background: brandColor(b), color: '#fff' }}>
                    {brandInitial(b)}
                  </span>
                  <div className="who">
                    <div className="nm">{b.brandName}</div>
                    <div className="mt">{`${b.brandDescription ?? ''} · ${vcardCount} vCards`}</div>
                  </div>
                  <div className="side">
                    {editable ? (
                      <button
                        type="button"
                        className="btn outline sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/admin/brands/${b.brandKey}`)
                        }}
                      >
                        {Icons.edit} Bearbeiten
                      </button>
                    ) : (
                      <button type="button" className="btn ghost sm" disabled>
                        {Icons.lock} Gesperrt
                      </button>
                    )}
                    {editable && <span className="row-go">{Icons.go}</span>}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
    </>
  )
}
